// src/components/EmergencyContactManager.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard } from "lucide-react";
import api from "../lib/api"; // ← プロジェクトに合わせて ../lib/api などに変更
import "./EmergencyContactManager.css";
import { isPresident, isDeptHeadLevel } from "../lib/positionRank";


/* ========== helpers ========== */

// APIの返りを配列に正規化
function normalizeArray(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.results)) return data.results; // DRF paginate
  if (Array.isArray(data.data)) return data.data;       // axios で包まれている等
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.people)) return data.people; // GET /api/employees/in-my-group/
  // { deptA:[...], deptB:[...] } 形式も flatten
  const vals = Object.values(data);
  if (vals.length && vals.every(v => typeof v === "object")) {
    const flat = vals.flatMap(v => Array.isArray(v) ? v : []);
    if (flat.length) return flat;
  }
  return [];
}

// 部署名の取り出し（API側の表記揺れに対応）
function getDeptKey(emp) {
  const deptKey = (
    emp?.dept_name ??                 // values(..., dept_name=F("department__departmentName"))
    emp?.department_name ??
    emp?.department?.departmentName ?? // ネストが Department のとき
    emp?.department?.name ??           // name で来るケース
    emp?.departmentName ?? emp?.departmentname ??
    emp?.department_label ??
    emp?.departmentId ?? emp?.department_id ??
    "未定"
    
  );

  return deptKey || "未定";
  
}

// 画面上の“インスタンス”を識別するキー（同じ社員でも複製できるように）
const uid = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
function instanceKey(emp) {
  return emp?.__uid || `${emp?.memberId ?? emp?.id ?? ""}`;
}

// HTMLが返った（=APIに届かずSPAに吸われた）か判定
function isHtmlResponse(res) {
  const ct = (res.headers?.["content-type"] || res.headers?.["Content-Type"] || "").toLowerCase();
  return ct.includes("text/html");
}

// ついでにOSクリップボードにも入れておきたい時の軽い関数（任意）
async function copyTextToClipboard(text = "") {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text ?? "");
    ta.readOnly = true;
    ta.style.position = "fixed";
    ta.style.top = "-10000px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch { return false; }
}

// 役職判定は共通のランク方式を使用（組織図・社員連絡先と統一）
// 部長〜本部長（社長・取締役は含めない）＝自部署配下の全課に表示する対象

// 部署ツリー（/departments/tree/）から親子マップを構築
function buildDeptMaps(treeRoots) {
  const parentOf = {};   // 子課名 -> 親部署名
  const childrenOf = {}; // 親部署名 -> [子課名...]
  const walk = (nodes, parentName) => {
    for (const n of nodes || []) {
      const name = n?.departmentName ?? n?.name;
      if (!name) continue;
      if (parentName) {
        parentOf[name] = parentName;
        (childrenOf[parentName] = childrenOf[parentName] || []).push(name);
      }
      walk(n.children, name);
    }
  };
  walk(treeRoots, null);
  return { parentOf, childrenOf };
}
// 同じ親部署に属する全課（自分含む）。親が不明ならその課のみ。
function siblingDepts(deptName, maps) {
  const parent = maps?.parentOf?.[deptName];
  if (!parent) return [deptName];
  return maps.childrenOf[parent] || [deptName];
}

/* ========== component ========== */

export default function EmergencyContactManager() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  // { "開発部": [empInstance, ...], ... }
  const [groups, setGroups] = useState({});
  const [departments, setDepartments] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [filter, setFilter] = useState("");

  const [searchParams] = useSearchParams();
  const filterDeptsParam = searchParams.get('filterDepts');
  const initialFilterDepts = useMemo(() => {
    if (!filterDeptsParam) return [];
    return filterDeptsParam.split(',').map(name => decodeURIComponent(name));
  }, [filterDeptsParam]);

  // 1) データ取得（by-department → employees → in-my-group の順にフォールバック）
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        let data = [];

        try {
          const r1 = await api.get("/employees/by-department/");
          if (!isHtmlResponse(r1)) data = normalizeArray(r1.data);
        } catch {}

        if (!data.length) {
          try {
            const r2 = await api.get("/employees/", { params: { page_size: 1000 } });
            if (!isHtmlResponse(r2)) data = normalizeArray(r2.data);
          } catch {}
        }

        if (!data.length) {
          try {
            const r3 = await api.get("/employees/in-my-group/");
            if (!isHtmlResponse(r3)) data = normalizeArray(r3.data);
          } catch {}
        }

        if (!data.length) {
          setErr("表示できる社員データがありません（0件）。");
          setDepartments([]);
          setGroups({});
          return;
        }

        // 部署配列
        //const depts = [...new Set(data.map(getDeptKey))].sort();
        let filteredData = data;
        if (initialFilterDepts.length > 0) {
            filteredData = data.filter(emp => {
                const deptName = getDeptKey(emp);
                return initialFilterDepts.includes(deptName);
            });
        }
        
        let finalDepts = [...new Set(filteredData.map(getDeptKey))].filter(Boolean);
        finalDepts = finalDepts.filter(d => d !== "未定").sort();
        if (filteredData.some(e => getDeptKey(e) === "未定")) {
            finalDepts.push("未定");
        }
        setDepartments(finalDepts);

        const g = finalDepts.reduce((acc, d) => {
          acc[d] = filteredData
            .filter(e => getDeptKey(e) === d)
            .map(e => ({ ...e, __uid: uid() }));
          return acc;
        }, {});

        // 自動表示ルール：社長→全課、部長→自部署配下の全課
        // （候補は全社員 data から。表示先は画面に出ている finalDepts のみ）
        let maps = { parentOf: {}, childrenOf: {} };
        try {
          const rt = await api.get("/departments/tree/");
          if (!isHtmlResponse(rt)) maps = buildDeptMaps(normalizeArray(rt.data));
        } catch {}

        const alreadyIn = (dept, emp) =>
          (g[dept] || []).some(x => (x.memberId ?? x.id) === (emp.memberId ?? emp.id));
        const addAuto = (dept, emp, reason) => {
          if (dept === "未定" || !finalDepts.includes(dept)) return;
          if (alreadyIn(dept, emp)) return;
          g[dept] = [...(g[dept] || []), { ...emp, __uid: uid(), __auto: true, __autoReason: reason }];
        };
        data.forEach(emp => {
          if (isPresident(emp.position)) {
            finalDepts.forEach(d => addAuto(d, emp, "社長"));
          } else if (isDeptHeadLevel(emp.position)) {
            const home = getDeptKey(emp);
            siblingDepts(home, maps).forEach(d => { if (d !== home) addAuto(d, emp, "部長"); });
          }
        });
        setGroups(g);

        
      } catch (e) {
        const s = e?.response?.status;
        setErr(s ? `APIエラー (${s})` : "ネットワークエラー");
        setDepartments([]);
        setGroups({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 2) 検索フィルタ（社員名に部分一致）
  const viewGroups = useMemo(() => {
    if (!filter) return groups;
    const q = filter.toLowerCase();
    const ng = {};
    for (const d of Object.keys(groups)) {
      ng[d] = (groups[d] || []).filter(emp =>
        String(emp?.name ?? "").toLowerCase().includes(q)
      );
    }
    return ng;
  }, [groups, filter]);

  // 3) 複製：Copy で同じ部署に“もう1枚”作る（Pasteボタン不要）
  const duplicateInGroup = (dept, emp) => {
    const clone = { ...emp, __uid: uid(), __isClone: true };
    // OSクリップボードにも文字列を入れておく（任意）
    copyTextToClipboard(`${emp?.name ?? ""}${emp?.memberId ? ` (${emp.memberId})` : ""}`.trim());
    setGroups(prev => ({ ...prev, [dept]: [ ...(prev[dept] || []), clone ] }));
  };

  // 4) D&D 移動（“1枚だけ”を instanceKey で移動）
  const handleDragStart = (emp) => setDragging(emp);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, targetDept) => {
    e.preventDefault();
    if (!dragging) return;

    const dragKey = instanceKey(dragging);
    setGroups(prev => {
      const next = { ...prev };

      // すべての部署から、その“1枚”だけ除去
      Object.keys(next).forEach(d => {
        next[d] = (next[d] || []).filter(x => instanceKey(x) !== dragKey);
      });

      // 表示上の部署名フィールドも揃える
      const moved = { ...dragging };
      if ("dept_name" in moved) moved.dept_name = targetDept;
      if ("department_name" in moved) moved.department_name = targetDept;
      if ("department" in moved && typeof moved.department === "object") {
        moved.department = { ...(moved.department || {}), name: targetDept };
      }

      next[targetDept] = [ ...(next[targetDept] || []), moved ];
      return next;
    });

    setDragging(null);
  };

  // 5) （任意）1枚だけ削除ボタン
  const removeInstance = (dept, emp) => {
    const k = instanceKey(emp);
    setGroups(prev => ({
      ...prev,
      [dept]: (prev[dept] || []).filter(e => instanceKey(e) !== k),
    }));
  };

  {/*}
  const handleReload = () => window.location.reload();
  
    if (loading) return <div className="ecm-page"><div className="ecm-info">読み込み中…</div></div>;
    if (err) {
      return (
        <div className="ecm-page">
          <div className="ecm-error">エラー: {err}</div>
          <div className="ecm-toolbar">
            <button className="btn" onClick={handleReload}>再読込</button>
            <Link to="/dashboard" className="btn btn-secondary">ダッシュボードに戻る</Link>
          </div>
        </div>
      );
    }
  */}
  // 保存：現在の groups を社員IDと部署名だけにして送信
  
  const handleSave = async () => {
    const isConfirmed = window.confirm("保存してもよろしいですか？");
    if (!isConfirmed) {
        return;
    }
    setLoading(true);
    setErr("");
    try {
  // すべての部署のすべての社員インスタンスをフラット化
  // 自動表示（社長・部長ルール）のカードは保存対象から除外（本所属を上書きしないため）
      const allEmployees = Object.values(groups).flat().filter(e => !e.__auto);

      const map = new Map();
      allEmployees.forEach(emp => {
        const key = emp.id || emp.memberId;
        if (!key) return; // IDがないのは無視
        map.set(key, {id: emp.id ?? null, memberId: emp.memberId ?? null, departmentName: getDeptKey(emp)});    
      });

      const employeesToUpdate = Array.from(map.values());

      // PUTまたはPOSTリクエストをバックエンドに送信して一括更新
      // 注意: バックエンドに対応するAPIエンドポイントが必要です。
      const response = await api.post("/employees/bulk_update/", { employees: employeesToUpdate });

      // 成功したレスポンスを処理
      if (response.status === 200) {
        alert("変更が正常に保存されました。");
      } else {
        setErr("保存に失敗しました。サーバーエラー。");
      }
    } catch (e) {
      const s = e?.response?.status;
      setErr(s ? `APIエラー (${s})` : "ネットワークエラー");
    } finally {
      setLoading(false);
    }
  };
  
  
  return (
    <div className="ecm-page">
      <div
        className="ecm-page-header"
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <h2 className="ecm-title" style={{ margin: 0 }}>
          社員管理
        </h2>
        <Link
          to="/admin/employee-card-setting"
          className="ecm-card-setting-link"
          title="社員証・会社設定（表示項目・会社情報・スケジュール・打刻15分丸め）"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 88,
            padding: "10px 14px",
            border: "1px solid #c5c8ce",
            borderRadius: 10,
            background: "#f8f9fb",
            color: "#1e293b",
            textDecoration: "none",
            fontSize: "0.75rem",
            lineHeight: 1.2,
          }}
        >
          <CreditCard size={28} strokeWidth={1.5} aria-hidden />
          <span style={{ marginTop: 6, fontWeight: 600 }}>社員証・会社設定</span>
        </Link>
      </div>
            {/*}
            <div className="ecm-toolbar">
              <input
                className="ecm-input"
                placeholder="社員名で検索…"
                onChange={(e) => setFilter(e.target.value)}
              />
              <button className="btn" onClick={handleReload}>再読込</button>
              <button className="btn btn-primary" onClick={handleSave}>保存</button>
              <button className="btn-back" onClick={() => navigate('/dashboard')}>戻る</button>
              
            </div>
            */}
      <div className="ecm-grid">
        
        {departments.map((dept) => (
            
          <div
            key={dept}
            className="ecm-group"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, dept)}
          >
            <div className="ecm-group-title">{dept}</div>
            <div style={{ fontSize: '0.85em', color: '#1a1a1a', fontWeight: 'bold' }}>
                    {dept}
            </div>

            {(viewGroups[dept] || []).map((emp) => (
              <div
                key={instanceKey(emp)}
                className="ecm-cell"
                draggable={!emp.__auto}
                title={emp.__auto ? `${emp.__autoReason}ルールで自動表示（移動・削除不可）` : "ドラッグで他部署に移動 / Copyで同じ部署に複製"}
                onDragStart={() => { if (!emp.__auto) handleDragStart(emp); }}
                style={emp.__auto ? { background: "#eef2ff" } : undefined}
              >
                <div className="ecm-cell-main">

                  <span className="ecm-name">
                    {emp?.name ?? "(無名)"}{" "}
                    {emp?.memberId || emp?.id ? `(${emp?.memberId ?? emp?.id})` : ""}
                  </span>
                  {emp.__auto && (
                    <span
                      className="ecm-auto-badge"
                      style={{ marginLeft: 6, fontSize: "0.7rem", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 6, padding: "0 6px" }}
                    >
                      {emp.__autoReason}
                    </span>
                  )}
                </div>
                {!emp.__auto && (
                  <div className="ecm-cell-actions" style={{ display:"flex", gap:8 }}>
                    <button className="link" onClick={() => duplicateInGroup(dept, emp)}>Copy</button>
                    <button className="link" onClick={() => removeInstance(dept, emp)}>×</button>
                  </div>
                )}
              </div>
            ))}

            {(!viewGroups[dept] || !viewGroups[dept].length) && (
              <div className="ecm-empty">該当なし</div>
            )}
          </div>
        ))}
      </div>
      <div className="button-group">
        <button className="btn-save" onClick={handleSave}>保存</button>
        <button className="btn-back" onClick={() => navigate('/admin/emergency_contact')}>戻る</button>
      </div>
    </div>
  );
}
